import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { licensesApi } from '../api'
import { useAuthStore } from '../store/authStore'
import { SCOPE_EDGE_LICENSES } from '../config/apps'
import { PERPETUAL_EXPIRY_DATE, formatExpiry } from '../config/license'
import CloudLicenseModal from '../components/CloudLicenseModal'
import type { LicenseIssuance, LicenseType } from '../types'

const TYPE_COLORS: Record<LicenseType, string> = {
  Cloud: 'bg-blue-100 text-blue-700',
  Local: 'bg-purple-100 text-purple-700',
}

export default function Licenses() {
  const qc = useQueryClient()
  // 발급·삭제: Admin 또는 edge:licenses (다운로드는 Manager 도 가능 — 페이지 진입은 nav 가 거른다)
  const { can } = useAuthStore()
  const canManage = can(SCOPE_EDGE_LICENSES)
  const [showLocalModal, setShowLocalModal] = useState(false)
  const [reissueTarget, setReissueTarget] = useState<LicenseIssuance | null>(null)

  const [localForm, setLocalForm] = useState({ companyName: '', expiresAt: '', perpetual: false, maxNodeCount: '50', note: '' })

  const { data: issuances = [] } = useQuery({
    queryKey: ['licenses'],
    queryFn: () => licensesApi.getAll().then(r => r.data),
  })

  const localMutation = useMutation({
    mutationFn: () => licensesApi.issueLocal({
      companyName: localForm.companyName,
      expiresAt: localForm.perpetual ? PERPETUAL_EXPIRY_DATE : localForm.expiresAt,
      maxNodeCount: parseInt(localForm.maxNodeCount),
      note: localForm.note || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['licenses'] })
      setShowLocalModal(false)
      setLocalForm({ companyName: '', expiresAt: '', perpetual: false, maxNodeCount: '50', note: '' })
    },
  })

  const downloadMutation = useMutation({
    mutationFn: async (issuance: LicenseIssuance) => {
      const { data } = await licensesApi.download(issuance.id)
      const url = URL.createObjectURL(data)
      const a = document.createElement('a')
      a.href = url
      // LiveEdge 는 edge 폴더의 고정 파일명 license.lic.json 을 읽으므로 그 이름으로 저장 (rename 불필요)
      a.download = 'license.lic.json'
      a.click()
      URL.revokeObjectURL(url)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => licensesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['licenses'] }),
  })

  const isExpired = (expiresAt: string) => new Date(expiresAt) < new Date()

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold tracking-tight text-gray-900">라이센스 관리</h2>
        {canManage && (
          <button onClick={() => setShowLocalModal(true)}
            className="bg-purple-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-purple-500 transition">
            + Local 발급
          </button>
        )}
      </div>

      <div className="bg-surface rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs">
            <tr>
              <th className="text-left px-4 py-3">타입</th>
              <th className="text-left px-4 py-3">서버 / 회사</th>
              <th className="text-left px-4 py-3">발급일</th>
              <th className="text-left px-4 py-3">만료일</th>
              <th className="text-left px-4 py-3">Node 수</th>
              <th className="text-left px-4 py-3">상태</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {issuances.map((l: LicenseIssuance) => (
              <tr key={l.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[l.type]}`}>
                    {l.type}
                  </span>
                </td>
                <td className="px-4 py-3 font-medium text-gray-800">
                  {l.server?.name ?? l.companyName ?? '-'}
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs">
                  {new Date(l.issuedAt).toLocaleDateString('ko-KR')}
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs">
                  {formatExpiry(l.expiresAt)}
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">{l.maxNodeCount}</td>
                <td className="px-4 py-3">
                  {isExpired(l.expiresAt) ? (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-600">만료</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">유효</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => downloadMutation.mutate(l)}
                      className="text-blue-500 hover:text-blue-700 text-xs">다운로드</button>
                    {canManage && l.type === 'Cloud' && l.server && (
                      <button onClick={() => setReissueTarget(l)}
                        className="text-blue-500 hover:text-blue-700 text-xs">변경</button>
                    )}
                    {canManage && (
                      <button
                        onClick={() => { if (confirm('라이센스를 삭제하시겠습니까?')) deleteMutation.mutate(l.id) }}
                        className="text-red-400 hover:text-red-600 text-xs">삭제</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {issuances.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">발급된 라이센스가 없습니다.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Local 발급 모달 */}
      {showLocalModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-surface rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="font-semibold text-gray-800 mb-4">Local 라이센스 발급</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">회사명</label>
                <input value={localForm.companyName}
                  onChange={e => setLocalForm({ ...localForm, companyName: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="예: ACME Corporation" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs text-gray-500">만료일</label>
                  <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={localForm.perpetual}
                      onChange={e => setLocalForm({ ...localForm, perpetual: e.target.checked })} />
                    무기한
                  </label>
                </div>
                <input type="date" value={localForm.perpetual ? '' : localForm.expiresAt} disabled={localForm.perpetual}
                  onChange={e => setLocalForm({ ...localForm, expiresAt: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">최대 Node 수</label>
                <input type="number" value={localForm.maxNodeCount}
                  onChange={e => setLocalForm({ ...localForm, maxNodeCount: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">메모 (선택)</label>
                <input value={localForm.note}
                  onChange={e => setLocalForm({ ...localForm, note: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="고객사 요청 등" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => localMutation.mutate()}
                disabled={!localForm.companyName || (!localForm.perpetual && !localForm.expiresAt) || localMutation.isPending}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-500 transition disabled:opacity-50">
                {localMutation.isPending ? '발급 중…' : '발급'}
              </button>
              <button onClick={() => setShowLocalModal(false)}
                className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm">취소</button>
            </div>
          </div>
        </div>
      )}

      {/* Cloud 라이센스 변경(재발급) 모달 */}
      {reissueTarget?.server && (
        <CloudLicenseModal
          server={reissueTarget.server}
          current={reissueTarget}
          onClose={() => setReissueTarget(null)} />
      )}
    </div>
  )
}
